"use client";
import {
  createProductFormSchema,
  shouldShowForm,
} from "@/functions/ProductAddOns/ProductAddOns";
import "./productOptions.scss";
import { useUpdateSearchParameters } from "@/hooks/useUpdateParameters/useUpdateParameters";
import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import Cookies from "js-cookie";
import { message } from "antd";
import { updateCartCount, displayCart } from "@/redux/Slices/cartSlice";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import FormInput from "@/components/fashion/common/FormInput";
import BuyModal from "../BuyModal";
import InstallmentsModal from "../InstallmentsModal/InstallmentsModal";

const ProductOptions = ({
  title,
  price,
  inStock,
  installments,      // commission_installment_plans (BAFL)
  allBankPlans,      // all_bank_installment_plans
  options,
  selectedVarient,
  category,
  is_cnic,
  is_iemi1,
  is_iemi2,
  is_bnpl,
  quantity_limit,
}) => {
  const dispatch = useDispatch();
  const { updateParams } = useUpdateSearchParameters();

  const [qty, setQty] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
  const [formData, setFormData] = useState(null);
  const [isInstallmentModalOpen, setIsInstallmentModalOpen] = useState(false);

  // BAFL plan selection — no default; user must choose in the modal
  const [selectedInstallment, setSelectedInstallment] = useState("");
  const [selectedInterestRate, setSelectedInterestRate] = useState(null);

  // All Bank plan selection
  const [selectedAllBankPlan, setSelectedAllBankPlan] = useState(null);

  // Which plan type was last selected — drives card_type in payload
  const [activePlanType, setActivePlanType] = useState(null); // "bafl" | "all_bank"

  const showForm = shouldShowForm(is_cnic, is_iemi1, is_iemi2);

  const resolveItemType = () => {
    const cat = Array.isArray(category) ? category[0] : category;
    const fromProduct = cat?.handle ?? cat?.title ?? null;
    const fromCookie = Cookies.get("collection_name");
    return fromProduct || fromCookie || null;
  };

  const ensureUserAgentForPayload = () => {
    const fromCookie = Cookies.get("user_agent");
    if (fromCookie) return fromCookie;
    const sessionId =
      Date.now().toString() +
      Math.floor(Math.random() * 1000000000000).toString();
    Cookies.set("user_agent", sessionId);
    return sessionId;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(createProductFormSchema(is_cnic, is_iemi1, is_iemi2)),
    mode: "onSubmit",
  });

  const handleOptionClick = (option, value) => {
    updateParams({ key: option, value });
  };

  // Called from InstallmentsModal when user picks a BAFL plan
  const handleBAFLInstallmentSelect = (value) => {
    setSelectedInstallment(value);
    setSelectedAllBankPlan(null);
    setActivePlanType("bafl");

    // Capture interest_rate for the selected BAFL plan
    const plan = installments?.plans?.find((p) => p.installment_plan === value);
    setSelectedInterestRate(plan?.interest_rate ?? null);
  };

  // Called from InstallmentsModal when user picks an All Bank plan
  const handleAllBankInstallmentSelect = (value) => {
    setSelectedAllBankPlan(value);
    setSelectedInstallment("");
    setSelectedInterestRate(null);
    setActivePlanType("all_bank");
  };

  const toggleBuyModal = () => setIsBuyModalOpen((v) => !v);
  const toggleInstallmentModal = () => setIsInstallmentModalOpen((v) => !v);

  const handleInstallmentModal = () => {
    setIsBuyModalOpen(false);
    setIsInstallmentModalOpen(true);
  };

  /**
   * Core add-to-cart function.
   *
   * paymentType:
   *   "full"      → no BNPL fields
   *   "bafl"      → is_bnpl:true, card_type:"bafl_plans", installment_plan, interest_rate
   *   "all_bank"  → is_bnpl:true, card_type:"all_bank_plans", installment_plan
   */
  const handleAddtoCart = async (paymentType = "full") => {
    try {
      if (showForm && !formData) return;

      setIsLoading(true);

      const defaultgeoLocationCountry = Cookies.get("geoLocation");
      const storeId = Cookies.get("fashionCurrencyId") || "23";

      const lineItem = {
        add_ons: [],
        quantity: qty,
        item_type: resolveItemType(),
        has_insurance: false,
        variant_price_inventory: selectedVarient?.variant_detail?.variant_id,
      };

      if (paymentType === "bafl") {
        lineItem.is_bnpl = true;
        lineItem.card_type = "bafl_plans";
        lineItem.installment_plan = selectedInstallment;
        if (selectedInterestRate !== null && selectedInterestRate !== undefined) {
          lineItem.interest_rate = selectedInterestRate;
        }
      } else if (paymentType === "all_bank") {
        lineItem.is_bnpl = true;
        lineItem.card_type = "all_bank_plans";
        lineItem.installment_plan = selectedAllBankPlan;
        lineItem.interest_rate = selectedInterestRate;
      }
      // "full" → no BNPL fields added

      const body = {
        storefront: storeId,
        storefront_type: "Fashion",
        line_item: lineItem,
        ip_country: defaultgeoLocationCountry || "Pakistan",
        user_agent: ensureUserAgentForPayload(),
      };

      if (formData?.cnic) body.line_item.cnic = formData.cnic;
      if (formData?.iemi1) body.line_item.imei_1 = formData.iemi1;
      if (formData?.iemi2) body.line_item.imei_2 = formData.iemi2;

      const url = process.env.NEXT_PUBLIC_BACKEND + `/order/add_item_in_cart`;
      const cartId = Cookies.get("fashion_cart_id");

      const fetchPayload = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      };
      if (cartId) fetchPayload.headers.cartid = cartId;

      const response = await fetch(url, fetchPayload);
      const result = await response.json();

      setIsLoading(false);

      if (!response.ok) {
        if (result.data?.detail === "Maximum quantity exceeded") {
          message.error(result?.detail);
          return;
        }
        message.error("Failed to add product");
        return;
      }

      message.success("Product Added to Cart!");
      Cookies.set("fashion_cart_id", result?.cartid);
      dispatch(updateCartCount(result?.line_items.length));

      if (showForm) reset();

      return result.cartid;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      message.error(`Failed to add item to cart: ${errorMessage}`);
      setIsLoading(false);
    }
  };



  // BAFL installment checkout
  const handleBAFLInstallmentBuy = async () => {
    toggleInstallmentModal();
    const cartId = await handleAddtoCart("bafl", selectedInterestRate);
    if (cartId) {
      dispatch(updateCartCount(1));
      dispatch(displayCart(true));
    }
  };

  // All Bank installment checkout
  const handleAllBankInstallmentBuy = async () => {
    toggleInstallmentModal();
    const cartId = await handleAddtoCart("all_bank");
    if (cartId) {
      dispatch(updateCartCount(1));
      dispatch(displayCart(true));
    }
  };

  // Unified checkout from InstallmentsModal — routes to correct handler
  const handleInstallmentBuy = async () => {
    if (activePlanType === "all_bank") {
      await handleAllBankInstallmentBuy();
    } else {
      await handleBAFLInstallmentBuy();
    }
  };

  const onSubmit = (data) => {
    setFormData(data);
    toggleBuyModal();
  };

  const handleQty = (type) => {
    const hasLimit = quantity_limit !== null;
    if (type === "dec" && qty > 1) {
      setQty((prev) => prev - 1);
    } else if (hasLimit && type === "inc") {
      if (qty < quantity_limit) setQty((prev) => prev + 1);
    } else if (type === "inc") {
      setQty((prev) => prev + 1);
    }
  };

  // Dynamically determine pill options based on value count
  // Options with fewer values (e.g., size with S,M,L,XL) are displayed as pills
  const pillOptions = options?.filter((opt) => {
    const valueCount = opt.values?.split(",").length || 0;
    return valueCount <= 10; // Treat options with 10 or fewer values as pills
  }) || [];

  console.log("pillOptions?????????",pillOptions)
  
  const displayOptions = options?.filter((opt) => {
    const valueCount = opt.values?.split(",").length || 0;
    return valueCount > 10; // Options with more values shown as text
  }) || [];


  console.log("displayOptions***********",displayOptions)

  const hasBNPL = is_bnpl === "Yes" && (installments?.plans?.length > 0 || allBankPlans?.plans?.length > 0 || (Array.isArray(allBankPlans) && allBankPlans.length > 0));

  return (
    <>
      <div className="product-options">
        {/* Display options (Color, Fabric, etc.) */}
        {displayOptions.length > 0 && (
          <div className="display-options-block">
            {displayOptions.map(({ id, name }) => {
              const selectedValue = selectedVarient[`option${options.indexOf(options.find(o => o.id === id)) + 1}`];
              return (
                <p key={id} className="display-option-line">
                  <span className="display-option-label">{name}:</span>{" "}
                  <span className="display-option-value">{selectedValue || "—"}</span>
                </p>
              );
            })}
          </div>
        )}

        {/* Size pills */}
        {pillOptions.length > 0 && (
          <div className="pill-options-block">
            {pillOptions.map(({ id, name, values }) => {
              const optionIndex = options.findIndex((o) => o.id === id) + 1;
              return (
                <div key={id} className="pill-option-group">
                  <h3 className="pill-option-label">{name.toUpperCase()}</h3>
                  <div className="pill-option-values">
                    {values.split(",").map((value, vi) => (
                      <p
                        key={`${value}-${vi}`}
                        onClick={() => handleOptionClick(`option${optionIndex}`, value)}
                        style={{
                          backgroundColor:
                            selectedVarient[`option${optionIndex}`] === value ? "#333333" : "transparent",
                          color:
                            selectedVarient[`option${optionIndex}`] === value ? "white" : "",
                        }}
                        className="option-value-pill"
                      >
                        {value}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="size-guide-row">
              <span className="size-guide-icon">📏</span>
              <a href="#" className="size-guide-link">Size Guide</a>
            </div>

            {!inStock && <p className="out-of-stock">Out of Stock</p>}
          </div>
        )}

        {/* Additional form fields (CNIC / IMEI) */}
        {showForm && (
          <div className="formContainer">
            <p className="label">Additional Information Required</p>
            <form onSubmit={handleSubmit(onSubmit)} className="form">
              {is_cnic && (
                <FormInput
                  name="cnic"
                  register={register}
                  placeholder="CNIC (12345-1234567-1)*"
                  type="text"
                  error={errors.cnic}
                />
              )}
              {is_iemi1 && (
                <FormInput
                  name="iemi1"
                  register={register}
                  placeholder="IMEI 1 (15 digits)*"
                  error={errors?.iemi1}
                  type="text"
                />
              )}
              {is_iemi2 && (
                <FormInput
                  name="iemi2"
                  register={register}
                  placeholder="IMEI 2 (15 digits)*"
                  error={errors?.iemi2}
                  type="text"
                />
              )}
            </form>
          </div>
        )}

        {/* Quantity & Add to Cart */}
        <h3 className="quantity-heading">QUANTITY</h3>
        <div className="product-buttons">
          <div className="product-qty-container">
            <span className="product-qty">{qty}</span>
            <div className="qty-controls">
              <span onClick={() => handleQty("inc")} className="qty-btn">+</span>
              <span onClick={() => handleQty("dec")} className="qty-btn">−</span>
            </div>
          </div>
          <button
            onClick={showForm ? handleSubmit(onSubmit) : toggleBuyModal}
            disabled={!inStock || isLoading}
            className="add-to-cart-button"
            style={{
              background: !inStock ? "#808080" : "",
              border: !inStock ? "none" : "",
              cursor: !inStock ? "not-allowed" : "",
            }}
          >
            {isLoading ? "ADDING..." : "ADD TO CART"}
          </button>
        </div>
      </div>

      {/* BuyModal — Pay in Full vs Buy Now Pay Later */}
      {isBuyModalOpen && (
        <BuyModal
          addProduct={async () => {
            const cartId = await handleAddtoCart("full");
            if (cartId) {
              dispatch(displayCart(true));
            }
          }}
          isBNPL={hasBNPL ? "Yes" : "No"}
          isOpen={isBuyModalOpen}
          onClose={toggleBuyModal}
          handleInstallmentModal={handleInstallmentModal}
        />
      )}

      {/* InstallmentsModal — BAFL + All Bank plans */}
      {isInstallmentModalOpen && (
        <InstallmentsModal
          installments={installments}
          allBankPlans={allBankPlans}
          isOpen={isInstallmentModalOpen}
          onClose={toggleInstallmentModal}
          price={price}
          title={title}
          selectedInstallment={selectedInstallment}
          selectedAllBankPlan={selectedAllBankPlan}
          selectedInterestRate={selectedInterestRate}
          onSelectBAFL={handleBAFLInstallmentSelect}
          onSelectAllBank={handleAllBankInstallmentSelect}
          handleBuy={handleInstallmentBuy}
        />
      )}
    </>
  );
};

export default ProductOptions;
